/**
 * Modal "Auto-créer les options CS"
 *
 * Détecte les options FI qui ont des étudiants type_formation=CS importés
 * mais pour lesquelles aucune option CS séparée n'existe encore. Permet de
 * dupliquer plusieurs options en lot vers leur version CS, avec toutes les
 * matières par défaut (vous pouvez ensuite affiner par option via le bouton
 * Copy individuel).
 *
 * READ-ONLY pour la détection. La duplication ne touche pas aux notes.
 */
import React, { useState, useEffect } from 'react';
import { X, Copy, Loader, Info, CheckCircle2, AlertCircle, Users } from 'lucide-react';
import apiClient from '../../config/api.config';

interface DetectionItem {
  option_id: number;
  option_code: string;
  option_nom: string;
  pole_nom: string | null;
  parcours_nom: string | null;
  code_cs_propose: string;
  option_cs_existe: boolean;
  nb_etudiants_cs_total: number;
  nb_par_niveau: Record<string, number>;
}
interface DetectionResponse {
  options_avec_etudiants_cs: DetectionItem[];
  total_options: number;
  total_etudiants_cs: number;
  total_deja_dupliquees: number;
}
interface ResultatDuplication {
  option_id: number;
  option_nom: string;
  success: boolean;
  message: string;
  actions?: any;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: (msg: string) => void;
}

const AutoCreerOptionsCSModal: React.FC<Props> = ({ open, onClose, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detection, setDetection] = useState<DetectionResponse | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<ResultatDuplication[]>([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setResults([]);
    setDone(false);
    apiClient
      .get('/options/detecter-options-cs/')
      .then((resp) => {
        const data = resp.data as DetectionResponse;
        setDetection(data);
        // Pré-sélection : options sans CS existante
        const ids = new Set<number>(
          data.options_avec_etudiants_cs
            .filter((o) => !o.option_cs_existe)
            .map((o) => o.option_id),
        );
        setSelectedIds(ids);
      })
      .catch((e: any) => {
        setError(e?.response?.data?.detail || e?.message || 'Erreur de chargement');
      })
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDupliquer = async () => {
    if (selectedIds.size === 0) {
      setError('Aucune option sélectionnée.');
      return;
    }
    setError(null);
    setRunning(true);
    setResults([]);
    const ids = Array.from(selectedIds);
    const allResults: ResultatDuplication[] = [];
    for (const id of ids) {
      const opt = detection?.options_avec_etudiants_cs.find((o) => o.option_id === id);
      try {
        const resp = await apiClient.post(`/options/${id}/dupliquer-pour-cs/`, {});
        allResults.push({
          option_id: id,
          option_nom: opt?.option_nom || '',
          success: true,
          message: resp.data?.message || 'OK',
          actions: resp.data?.actions,
        });
      } catch (e: any) {
        allResults.push({
          option_id: id,
          option_nom: opt?.option_nom || '',
          success: false,
          message: e?.response?.data?.detail || e?.message || 'Erreur',
        });
      }
      setResults([...allResults]);
    }
    setRunning(false);
    setDone(true);
    const okCount = allResults.filter((r) => r.success).length;
    onDone(`${okCount} / ${ids.length} option(s) CS créée(s) avec succès.`);
  };

  const handleClose = () => {
    setDetection(null);
    setResults([]);
    setSelectedIds(new Set());
    setDone(false);
    setError(null);
    onClose();
  };

  const items = detection?.options_avec_etudiants_cs || [];
  const sansCS = items.filter((o) => !o.option_cs_existe);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Users size={20} className="text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Auto-créer les options CS (Formation Continue)
            </h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm text-blue-900 dark:text-blue-100">
            <Info size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              Détecte les options ayant des étudiants <strong>type_formation=CS</strong> et crée une option CS séparée (suffixe <code>-CS</code>) pour chaque.
              <br />
              <strong>Toutes les matières actives sont dupliquées par défaut.</strong> Vous pourrez ensuite affiner option par option via le bouton <em>Dupliquer pour CS</em> individuel (sélection fine de matières).
              <br />
              Aucune note n'est touchée — opération atomique et idempotente.
            </div>
          </div>

          {loading && !detection ? (
            <div className="text-center py-8">
              <Loader size={24} className="animate-spin inline-block text-blue-600" />
              <p className="text-sm text-gray-500 mt-2">Détection en cours…</p>
            </div>
          ) : detection ? (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded">
                  <div className="text-2xl font-bold">{detection.total_options}</div>
                  <div className="text-xs text-gray-500">Options détectées</div>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded">
                  <div className="text-2xl font-bold">{detection.total_etudiants_cs}</div>
                  <div className="text-xs text-gray-500">Étudiants CS total</div>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded">
                  <div className="text-2xl font-bold">{detection.total_deja_dupliquees}</div>
                  <div className="text-xs text-gray-500">Déjà dupliquées</div>
                </div>
              </div>

              {sansCS.length === 0 && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-900 dark:text-green-100 flex items-center gap-2">
                  <CheckCircle2 size={16} />
                  Toutes les options ayant des étudiants CS ont déjà été dupliquées. Rien à faire.
                </div>
              )}

              <div className="border border-gray-200 dark:border-gray-700 rounded">
                {items.map((o) => {
                  const isSelected = selectedIds.has(o.option_id);
                  const result = results.find((r) => r.option_id === o.option_id);
                  return (
                    <div
                      key={o.option_id}
                      className={`flex items-center gap-3 px-3 py-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0 ${
                        o.option_cs_existe ? 'opacity-60' : ''
                      } ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(o.option_id)}
                        disabled={o.option_cs_existe || running}
                        className="rounded"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {o.option_nom}
                          <span className="ml-2 text-xs font-mono text-gray-500">→ {o.code_cs_propose}</span>
                          {o.option_cs_existe && (
                            <span className="ml-2 text-xs text-green-700 dark:text-green-400">(déjà créée)</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-0.5">
                          <span>{o.pole_nom}{o.parcours_nom ? ` / ${o.parcours_nom}` : ''}</span>
                          <span>•</span>
                          <span>{o.nb_etudiants_cs_total} étudiants CS</span>
                          {Object.entries(o.nb_par_niveau).map(([niv, nb]) => (
                            <span key={niv} className="px-1.5 py-0 bg-gray-100 dark:bg-gray-700 rounded">
                              {niv}: {nb}
                            </span>
                          ))}
                        </div>
                      </div>
                      {result && (
                        <div className="flex items-center gap-1">
                          {result.success ? (
                            <CheckCircle2 size={16} className="text-green-600" />
                          ) : (
                            <AlertCircle size={16} className="text-red-600" />
                          )}
                          <span className={`text-xs ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                            {result.success
                              ? `${result.actions?.matieres_crees || 0}m · ${result.actions?.ues_creees || 0}ue · ${result.actions?.classes_creees || 0}c`
                              : 'Erreur'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-900 dark:text-red-100">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 rounded-b-lg">
          <button onClick={handleClose} disabled={running} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            {done ? 'Fermer' : 'Annuler'}
          </button>
          {!done && (
            <button
              onClick={handleDupliquer}
              disabled={loading || running || selectedIds.size === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded"
            >
              {running ? <Loader size={16} className="animate-spin" /> : <Copy size={16} />}
              Dupliquer ({selectedIds.size}) en lot
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoCreerOptionsCSModal;
